import { ReactNode, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import MicIcon from '@mui/icons-material/Mic';
import CloudIcon from '@mui/icons-material/Cloud';
import RouterIcon from '@mui/icons-material/Router';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RuleIcon from '@mui/icons-material/Rule';
import SettingsIcon from '@mui/icons-material/Settings';

import type { FieldSpec } from '../../types';
import { FieldRenderer } from './FieldRenderer';
import { evalExpr, isVisible } from '../../utils/expr';
import { useCalculatorStore } from '../../store/calculatorStore';

/**
 * Inner contents of the adjustable-inputs sidebar.
 *
 * Rendered inside an MUI Drawer at App level. No positioning here —
 * the Drawer handles slide animation, width, and the push-content
 * side-effect. Reads/writes state from the calculator store so
 * App.tsx can render it as a one-liner.
 */

const ICONS: Record<string, ReactNode> = {
  people: <PeopleIcon />,
  chat: <ChatIcon />,
  mic: <MicIcon />,
  cloud: <CloudIcon />,
  router: <RouterIcon />,
  timeline: <TimelineIcon />,
  groups: <GroupsIcon />,
  notifications: <NotificationsIcon />,
  rule: <RuleIcon />,
  settings: <SettingsIcon />,
};
const iconFor = (name?: string): ReactNode =>
  (name && ICONS[name]) ?? <SettingsIcon />;

export function CalculatorSidebar() {
  const schema = useCalculatorStore((s) => s.schema);
  const params = useCalculatorStore((s) => s.params);
  const setParams = useCalculatorStore((s) => s.setParams);
  const hints = useCalculatorStore((s) => s.hints);
  const setSidebarOpen = useCalculatorStore((s) => s.setSidebarOpen);

  const [advancedExpanded, setAdvancedExpanded] = useState<
    Record<string, boolean>
  >({});

  if (!schema) return null;

  const updateParam = (name: string, value: unknown) =>
    setParams({ ...params, [name]: value });

  const renderFields = (fields: FieldSpec[]) =>
    fields.map((f) => (
      <FieldRenderer
        key={f.name}
        spec={f}
        value={params[f.name]}
        onChange={(v) => updateParam(f.name, v)}
        params={params}
        hints={hints}
        models={schema.models ?? null}
      />
    ));

  const toggleAdvanced = (name: string) =>
    setAdvancedExpanded((p) => ({ ...p, [name]: !p[name] }));

  return (
    <Box
      className="no-print"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // --- Compact density across the sidebar ------------------------------
        '& .MuiTypography-subtitle2': { fontSize: '0.8rem', fontWeight: 500 },
        '& .MuiTypography-body1, & .MuiTypography-body2': { fontSize: '0.8rem' },
        '& .MuiTypography-caption': { fontSize: '0.68rem' },
        '& .MuiInputBase-input': { fontSize: '0.78rem', padding: '4px 6px' },
        '& .MuiInputBase-root.MuiOutlinedInput-root': { height: 28 },
        '& .MuiInputLabel-root': { fontSize: '0.78rem' },
        '& .MuiFormControl-root': { mb: 1 },
        '& .MuiSlider-root': { padding: '10px 0' },
        '& .MuiSlider-thumb': { width: 14, height: 14 },
        '& .MuiSlider-valueLabel': { fontSize: '0.7rem' },
        '& .MuiAccordionSummary-root': { minHeight: 40, px: 1.25 },
        '& .MuiAccordionSummary-content': { my: '6px !important' },
        '& .MuiAccordionDetails-root': { p: 1.25 },
      }}
    >
      {/* Fixed header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          Adjust Inputs
        </Typography>
        <IconButton
          size="small"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close inputs"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {schema.calculator.sections
          .filter((section) => isVisible(section.visible_when, params))
          .map((section) => {
          const disabled = section.disabled_when
            ? evalExpr(section.disabled_when, params)
            : false;
          const hasAdvanced = section.advanced_fields.length > 0;
          const advOpen = advancedExpanded[section.workflow_name] ?? false;

          return (
            <Accordion
              key={section.workflow_name}
              defaultExpanded={false}
              disableGutters
              square
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      color: disabled ? 'text.disabled' : 'primary.main',
                      display: 'flex',
                      '& .MuiSvgIcon-root': { fontSize: 18 },
                    }}
                  >
                    {iconFor(section.icon)}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: disabled ? 'text.disabled' : 'text.primary' }}
                  >
                    {section.title}
                  </Typography>
                  {disabled && (
                    <Chip
                      label="Off"
                      size="small"
                      variant="outlined"
                      sx={{ ml: 'auto', height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {disabled && section.enable_default ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setParams({ ...params, ...section.enable_default })}
                    sx={{ mb: 1 }}
                  >
                    Enable
                  </Button>
                ) : (
                  <>
                    {renderFields(section.fields)}
                    {hasAdvanced && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Box
                          onClick={() => toggleAdvanced(section.workflow_name)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            py: 0.25,
                            color: 'text.secondary',
                            '&:hover': { color: 'text.primary' },
                          }}
                        >
                          <Typography variant="caption" sx={{ flexGrow: 1 }}>
                            Advanced
                          </Typography>
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transform: advOpen
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                              transition: 'transform 0.15s',
                              fontSize: 16,
                            }}
                          />
                        </Box>
                        {advOpen && (
                          <Box sx={{ mt: 0.75 }}>
                            {renderFields(section.advanced_fields)}
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
}
